import * as THREE from 'three';
import { CORNERS, elevationToY } from '../map/HexMetrics';
import { axialKey, axialToWorld, neighbor } from '../map/HexCoordinates';
import { HexCellData, cornerElevation } from '../map/HexSurface';
import { habitable, passable } from '../map/Movement';

/**
 * Line overlays for inspecting the grid.
 *
 * Both use the interpolated corner elevations that define the terrain surface,
 * so the lines lie on it rather than hovering: the surface between two corners
 * is exactly the straight edge shared by the triangles either side of it.
 *
 * Note on width — WebGL ignores LineBasicMaterial.linewidth in practically
 * every browser, so lines are always one pixel. The two overlays are told apart
 * by colour, not thickness.
 */

const HEX_OUTLINE_COLOR = 0x00e5ff;

/** Ściana nie do przejścia: biel, bo pod nią leżą czerwone i pomarańczowe płatki. */
const BLOCKED_EDGE_COLOR = 0xf2f2f2;
const CHUNK_BORDER_COLOR = 0xff3d6e;

/** Lifts the lines clear of the coplanar surface so they do not z-fight. */
const SURFACE_OFFSET = 0.02;

/**
 * Outlines of every cell. Deduplicated against the whole world, so a shared
 * edge is emitted by exactly one cell even where it straddles two chunks.
 */
export function createHexGridOverlay(
  world: Map<number, HexCellData>,
  members: HexCellData[]
): THREE.LineSegments {
  return buildEdgeLines(
    world,
    members,
    (cell, edge) => {
      const across = neighbor(cell, edge);
      return edge < 3 || !world.has(axialKey(across.q, across.r));
    },
    { color: HEX_OUTLINE_COLOR, opacity: 0.6 }
  );
}

/**
 * Outline of the chunk itself: every edge whose far side belongs to a different
 * chunk. Two neighbouring chunks each draw their side of a shared border, so it
 * is drawn twice — harmless at full opacity, which is why this one is not
 * transparent.
 */
export function createChunkBoundaryOverlay(
  world: Map<number, HexCellData>,
  members: HexCellData[]
): THREE.LineSegments {
  const inChunk = new Set(members.map((cell) => axialKey(cell.q, cell.r)));

  return buildEdgeLines(
    world,
    members,
    (cell, edge) => {
      const across = neighbor(cell, edge);
      return !inChunk.has(axialKey(across.q, across.r));
    },
    { color: CHUNK_BORDER_COLOR, opacity: 1 }
  );
}

/**
 * Krawędzie, których nie da się przekroczyć — ściana między dwoma polami.
 *
 * **Tylko tam, gdzie oba pola są do stania.** Krawędź przy wodzie albo przy
 * gąszczu byłaby powtórzeniem tego, co już mówi czerwony płatek na tamtym polu;
 * ta linia jest o czymś innym: o **różnicy wysokości**, przez którą nie da się
 * przejść między dwoma miejscami, na których stać wolno. To ona rysuje granie
 * i progi dolin, czyli te trasy, które wyglądają na przejezdne i nie są.
 *
 * Rysowana raz na krawędź: jej dwie strony należą do dwóch pól, więc rysuje ją
 * to o niższym numerze kierunku — dokładnie tak, jak robi to siatka heksów.
 */
export function createBlockedEdgeOverlay(
  world: Map<number, HexCellData>,
  members: HexCellData[]
): THREE.LineSegments {
  return buildEdgeLines(
    world,
    members,
    (cell, edge) => {
      const across = neighbor(cell, edge);
      const there = world.get(axialKey(across.q, across.r));
      if (!there) return false;

      // Ta sama para funkcji, którą rządzi się ruch — nie ich kopia.
      if (!habitable(cell) || !habitable(there)) return false;
      if (passable(cell, there)) return false;

      // Krawędź współdzielona: rysuje ją strona o niższym numerze kierunku.
      return edge < 3;
    },
    { color: BLOCKED_EDGE_COLOR, opacity: 0.95 }
  );
}

function buildEdgeLines(
  world: Map<number, HexCellData>,
  members: HexCellData[],
  shouldDraw: (cell: HexCellData, edge: number) => boolean,
  style: { color: number; opacity: number }
): THREE.LineSegments {
  const positions: number[] = [];

  for (const cell of members) {
    const { x, z } = axialToWorld(cell.q, cell.r);

    for (let edge = 0; edge < 6; edge++) {
      if (!shouldDraw(cell, edge)) continue;

      // Edge `edge` runs between corner `edge` and the next one round
      const from = edge;
      const to = (edge + 1) % 6;
      const yFrom = elevationToY(cornerElevation(world, cell, from)) + SURFACE_OFFSET;
      const yTo = elevationToY(cornerElevation(world, cell, to)) + SURFACE_OFFSET;

      positions.push(
        x + CORNERS[from].x, yFrom, z + CORNERS[from].z,
        x + CORNERS[to].x, yTo, z + CORNERS[to].z
      );
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3)
  );

  // depthTest off keeps the lines readable through hills — an x-ray view,
  // which is what makes them useful for checking layout.
  const material = new THREE.LineBasicMaterial({
    color: style.color,
    depthTest: false,
    transparent: style.opacity < 1,
    opacity: style.opacity,
  });

  const lines = new THREE.LineSegments(geometry, material);
  lines.renderOrder = 1;
  return lines;
}
